import { createApi } from '@reduxjs/toolkit/query/react'
import { customFetchBaseQuery } from '../app/baseQuery'
import type {
  FindAppointmentsPerClinicRequest,
  FindAppointmentsPerClinicRequest_Sorting,
  FindAppointmentsPerClinicResponse,
  FindAppointmentsRequest,
  FindAppointmentsRequest_Sorting,
  FindAppointmentsResponse,
} from '../../types/patient-api-gateway/api/patient/v1/patient_gateway_service'
import { TimeOfDay } from '../../types/patient-api-gateway/google/type/timeofday'
import { DateTime as GoogleDateTime } from '../../types/patient-api-gateway/google/type/datetime'

export type FindAppointmentsQueryArgs = {
  startTime?: GoogleDateTime
  endTime?: GoogleDateTime
  treatmentIds?: string[]
  clinicIds: string[]
  sorting?: FindAppointmentsRequest_Sorting
  startTimeOfDay?: TimeOfDay
  endTimeOfDay?: TimeOfDay
  pageToken?: string
  pageSize?: number
}

export type FindAppointmentsPerClinicQueryArgs = {
  startTime: GoogleDateTime
  endTime: GoogleDateTime
  clinicIds: string[]
  treatmentIds?: string[]
  maxAppointmentsPerClinic?: number
  sorting?: FindAppointmentsPerClinicRequest_Sorting
}

export const appointmentScreenApi = createApi({
  reducerPath: 'appointmentScreenApi',
  baseQuery: customFetchBaseQuery,
  tagTypes: ['Appointment', 'AppointmentPerClinic'],
  endpoints: (builder) => ({
    findAppointments: builder.query<
      FindAppointmentsResponse,
      FindAppointmentsQueryArgs
    >({
      query: ({
        startTime,
        endTime,
        treatmentIds,
        clinicIds,
        pageToken,
        pageSize,
        sorting,
      }) => ({
        url: '/api.patient.v1.PatientGatewayService/FindAppointments',
        method: 'POST',
        body: {
          startTime,
          endTime,
          treatmentIds,
          clinicIds,
          pageToken,
          pageSize,
          sorting,
        } satisfies FindAppointmentsRequest,
      }),
      extraOptions: {
        maxRetries: 3,
      },
      providesTags: (_, __, arg) => [
        'Appointment',
        {
          type: 'Appointment',
          id: JSON.stringify({
            clinicIds: arg.clinicIds?.join(','),
            startTime: arg.startTime,
            endTime: arg.endTime,
            treatmentIds: arg.treatmentIds?.join(','),
          }),
        },
      ],
      keepUnusedDataFor: 20,
    }),
    findAppointmentsPerClinic: builder.query<
      FindAppointmentsPerClinicResponse,
      FindAppointmentsPerClinicQueryArgs
    >({
      query: ({
        clinicIds,
        startTime,
        endTime,
        treatmentIds,
        maxAppointmentsPerClinic,
        sorting,
      }) => ({
        url: '/api.patient.v1.PatientGatewayService/FindAppointmentsPerClinic',
        method: 'POST',
        body: {
          startTime,
          endTime,
          treatmentIds,
          clinicIds,
          maxAppointmentsPerClinic,
          sorting,
        } satisfies FindAppointmentsPerClinicRequest,
      }),
      extraOptions: {
        maxRetries: 3,
      },
      providesTags: (_, __, arg) => [
        'AppointmentPerClinic',
        {
          type: 'Appointment',
          id: JSON.stringify({
            clinicIds: arg.clinicIds?.join(','),
            startTime: arg.startTime,
            endTime: arg.endTime,
            treatmentIds: arg.treatmentIds?.join(','),
          }),
        },
      ],
      keepUnusedDataFor: 0,
    }),
  }),
})

export const { useFindAppointmentsQuery, useFindAppointmentsPerClinicQuery } =
  appointmentScreenApi
